import config from "../../config";
import {Customer, CustomerEmailChannels, CustomerWithSession, OptInState, Profile, Session} from "./customer";
import {
  AddReceiptResponse,
  LoyaltyData,
  LoyaltyHistory,
  PartnerPromotion,
  QrCode,
  ShareId
} from "../loyalty/loaylty-data";
import {
  addReceipt,
  checkPartnerPromotion,
  consumePartnerPromotion,
  getHistory,
  getLoyaltyData,
  getLoyaltyQRCode,
  getShareId
} from "../loyalty/requests";
import {registerCustomer, RegisterData} from "./register";
import {acceptLoyalty, AcceptLoyaltyRequest} from "./accept-loyalty";
import {updateProfile} from "./updateProfile";
import {
  EmailCampaign,
  loadCustomerEmailChannels,
  loadOptInState,
  optOutAllCustomerEmailChannels,
  registerNewsletter,
  RegisterNewsletterRequest,
  resendVerificationMail,
  UpdateChannelsRequest,
  updateCustomerEmailChannels
} from "./newsletter";
import {updateAvatar} from "./avatar";
import {deleteCustomer} from "./delete";
import {dislike, getLikes, Like, like} from "./likes";
import {CustomerServiceError} from "./error";
import {changePassword, resetPassword, setPassword, verifyToken} from "./password";
import {getDeviceHeader} from "./device-header";

export const CUSTOMER_SERVICE_KEY = 'NY_CUSTOMER_SERVICE';
const NY_CUSTOMER_TOKEN = 'NY-CUSTOMER-TOKEN';
const NY_CUSTOMER_KEY = 'NY-CUSTOMER';

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    NY_CUSTOMER_SERVICE: ICustomerService;
  }
}

export interface ICustomerService {
  subscribeForCustomer(tag:string, callback:(c:Customer)=>void)
  subscribeForCustomerNotAvailable(tag:string, callback:()=>void)

  login(email:string, password:string, saveLogin:boolean):Promise<Customer>
  verify(): Promise<Customer>
  logout():Promise<boolean>
  resetPassword(email:string):Promise<boolean>
  verifyToken(token:string):Promise<string>
  setPassword(newPassword:string, token:string):Promise<boolean>
  changePassword(oldPassword:string, newPassword:string):Promise<boolean>

  getLoyaltyData():Promise<LoyaltyData>
  getLoyaltyHistory():Promise<LoyaltyHistory>
  getShareId():Promise<ShareId>
  getLoyaltyQRCode(rewardIds: string[], couponIds: string[]):Promise<QrCode>
  addReceipt(receiptNumber: string):Promise<AddReceiptResponse>
  checkPartnerPromotion(promotionCode: string):Promise<PartnerPromotion>
  consumePartnerPromotion(promotionCode: string): Promise<PartnerPromotion>

  registerCustomer(registerData:RegisterData, saveLogin:boolean):Promise<Customer>
  acceptLoyalty(req:AcceptLoyaltyRequest):Promise<Customer>
  deleteCustomer():Promise<boolean>
  updateProfile(profile:Profile):Promise<Customer>
  uploadAvatarImage(image:File, ):Promise<Customer>
  signupModelCasting(formData: FormData): Promise<boolean>

  subscribeForNewsletter(req: RegisterNewsletterRequest): Promise<boolean>
  resendVerificationEmail():Promise<boolean>
  loadOptInState(): Promise<OptInState>
  loadCustomerEmailChannels(token: string): Promise<CustomerEmailChannels>
  updateCustomerEmailChannels(token: string, req: UpdateChannelsRequest): Promise<boolean>
  optOutAllCustomerEmailChannels(token: string, campaign?: EmailCampaign): Promise<boolean>

  likeProduct(productId: string, variantId: string): Promise<boolean>
  dislikeProduct(productId: string, variantId: string): Promise<boolean>
  getLikesForCustomer(): Promise<Like[]>
}

export function getCustomerService(): ICustomerService {
  const cs = window[CUSTOMER_SERVICE_KEY];
  if (cs) {
    return cs as ICustomerService;
  }
  return null;
}

export class CustomerService implements ICustomerService {
  private initialized: boolean = false;
  private currentCustomer: Customer = null;
  private currentSession: Session;
  private readonly customerSubscriberMap: any = {};
  private readonly customerNaSubscriberMap: any = {};

  // noinspection JSUnusedGlobalSymbols
  public static get(): ICustomerService {
    const cs = window[CUSTOMER_SERVICE_KEY];
    if (cs) {
      return cs as ICustomerService;
    }
    return null
  }

  constructor() {
    this.currentCustomer = null;
    this.customerSubscriberMap = {};

    // noinspection JSIgnoredPromiseFromCall
    this.initCustomer()
  }

  private async initCustomer() {
    try {
      this.currentSession = await this.loadSessionFromApp();
      if (!this.currentSession) {
        this.currentSession = this.loadFromSessionStorage();
      }
      if (!this.currentSession) {
        this.currentSession = this.loadFromLocalStorage();
      }
      if (this.currentSession) {
        this.currentCustomer = await this.verify();
        this.notifyCustomerSubscribers(this.currentCustomer);
        this.saveToSessionStorage(this.currentSession);

        let event;
        if (typeof (Event) === 'function') {
          event = new Event("ny_user_updated");
        } else {
          // IE
          event = document.createEvent('Event');
          event.initEvent("ny_user_updated", true, true);
        }
        window.dispatchEvent(event);
      }
    } catch (e) {
      console.error("[customer-service] error while initializing customer: ", e);
    } finally {
      this.initialized = true;
      if (!this.currentCustomer) {
        this.notifyCustomerNaSubscribers();
      }
    }
  }

  public subscribeForCustomer(tag: string, callback: (c: Customer) => void) {
    if (this.currentCustomer) {
      callback(this.currentCustomer)
    }
    this.customerSubscriberMap[tag] = callback;
  }

  public subscribeForCustomerNotAvailable(tag: string, callback: () => void) {
    if (this.initialized && !this.currentCustomer) {
      callback()
    }
    this.customerNaSubscriberMap[tag] = callback;
  }

  //+++++++++++ AUTH ++++++++++++++++++++

  public async login(email: string, password: string, saveLogin: boolean = false): Promise<Customer> {
    const data = {email: email, password: password};
    const result = await fetch(`${config.customerServiceUrl}/customer/login`, {
      body: JSON.stringify(data),
      cache: 'no-cache',
      headers: {
        'content-type': 'application/json',
        'NY-DEVICE-DATA': getDeviceHeader(),
      },
      method: 'POST',
    });

    const json = await result.json();
    if (json.error) {
      throw new Error("[customer-service] error while logging in: " + json.error)
    }

    if (!result.ok) {
      throw new CustomerServiceError(json);
    }

    const customerSession = new CustomerWithSession(json);
    this.currentCustomer = customerSession.customer;
    this.currentSession = customerSession.session;

    this.saveToSessionStorage(this.currentSession);
    if (saveLogin) {
      this.saveToLocalStorage(this.currentSession)
    }
    this.notifyCustomerSubscribers(this.currentCustomer);
    return this.currentCustomer
  }

  public async verify(): Promise<Customer> {
    const result = await fetch(`${config.customerServiceUrl}/customer/${this.currentSession.customerId}/verify`, {
      cache: 'no-cache',
      headers: {
        'content-type': 'application/json',
        'NY-SESSION-TOKEN': this.currentSession.token,
        'NY-DEVICE-DATA': getDeviceHeader(),
      },
      method: 'POST',
    });

    const json = await result.json();
    if (json.error) {
      throw new Error("[customer-service] error while verifying session in: " + json.error)
    }

    if (!result.ok) {
      throw new CustomerServiceError(json);
    }

    const customerSession = new CustomerWithSession(json);
    this.currentCustomer = customerSession.customer;
    this.currentSession = customerSession.session;

    this.notifyCustomerSubscribers(this.currentCustomer);
    return this.currentCustomer
  }

  // noinspection JSUnusedGlobalSymbols
  async logout(): Promise<boolean> {
    const result = await fetch(`${config.customerServiceUrl}/customer/${this.currentSession.customerId}/logout`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'NY-SESSION-TOKEN': this.currentSession.token,
      }
    });
    this.removeCustomerFromStorage();
    if (!result.ok) {
      throw new Error("[customer-service] error while logging out: " + result.statusText);
    }
    return true;
  }

  async resetPassword(email: string): Promise<boolean> {
    return resetPassword(email);
  }

  async verifyToken(token:string):Promise<string>{
    return verifyToken(token);
  }

  async setPassword(newPassword: string, token: string): Promise<boolean> {
    return setPassword(newPassword, token);
  }

  async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    return changePassword(oldPassword, newPassword, this.currentSession);
  }

  //+++++++ LOYALTY +++++++++++
  public async getLoyaltyData(): Promise<LoyaltyData> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return getLoyaltyData(this.currentSession);
    }
    return null;
  };

  public async getLoyaltyHistory(): Promise<LoyaltyHistory> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return getHistory(this.currentSession);
    }
    return null;
  }

  async getShareId(): Promise<ShareId> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return getShareId(this.currentSession);
    }
    return null;
  }

  async getLoyaltyQRCode(rewardIds: string[], couponIds: string[]): Promise<QrCode> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return getLoyaltyQRCode(this.currentSession, rewardIds, couponIds);
    }
    return null;
  }

  async addReceipt(receiptNumber: string): Promise<AddReceiptResponse> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return addReceipt(this.currentSession, receiptNumber);
    }
    return null;
  }

  async checkPartnerPromotion(promotionCode: string): Promise<PartnerPromotion> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return checkPartnerPromotion(this.currentSession, promotionCode);
    }
    return null;
  }

  async consumePartnerPromotion(promotionCode: string): Promise<PartnerPromotion> {
    if (this.currentSession && this.currentCustomer && this.currentCustomer.loyalty) {
      return consumePartnerPromotion(this.currentSession, promotionCode);
    }
    return null;
  }

  //++++++++++ CUSTOMER +++++++++++

  async registerCustomer(registerData: RegisterData, saveLogin: boolean): Promise<Customer> {
    const customerSession = await registerCustomer(registerData);
    this.currentSession = customerSession.session;
    this.currentCustomer = customerSession.customer;

    this.saveToSessionStorage(this.currentSession);
    if (saveLogin) {
      this.saveToLocalStorage(this.currentSession)
    }
    this.notifyCustomerSubscribers(this.currentCustomer);

    return this.currentCustomer
  }

  async acceptLoyalty(req: AcceptLoyaltyRequest): Promise<Customer> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    this.currentCustomer = await acceptLoyalty(req, this.currentSession);
    this.notifyCustomerSubscribers(this.currentCustomer);
    return this.currentCustomer;
  }

  async updateProfile(profile: Profile): Promise<Customer> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    this.currentCustomer = await updateProfile(profile, this.currentSession);
    this.notifyCustomerSubscribers(this.currentCustomer);
    return this.currentCustomer;
  }

  async deleteCustomer(): Promise<boolean> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    const deleted = await deleteCustomer(this.currentSession);
    this.removeCustomerFromStorage();
    return deleted;
  }

  async uploadAvatarImage(image: File): Promise<Customer> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    if (!image) {
      throw new Error("no image given");
    }
    this.currentCustomer = await updateAvatar(image, this.currentSession);
    this.notifyCustomerSubscribers(this.currentCustomer);
    return this.currentCustomer;
  }

  public async signupModelCasting(formData: FormData): Promise<boolean> {
    const result = await fetch(`${config.customerServiceUrl}/model-casting/signup`,
      {
        credentials: 'omit',
        method: 'POST',
        body: formData
      });

    if (!result.ok) {
      throw ("[customer-service] error while signing-uo for model-casting: " + result.statusText);
    }
    return true;
  }

  //++++++++++++++ NEWSLETTER +++++++++++++

  async subscribeForNewsletter(req: RegisterNewsletterRequest): Promise<boolean> {
    return registerNewsletter(req);
  }

  async resendVerificationEmail(): Promise<boolean> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    return resendVerificationMail(this.currentSession);
  }

  async loadOptInState(): Promise<OptInState> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    return loadOptInState(this.currentSession);
  }

  async loadCustomerEmailChannels(token: string): Promise<CustomerEmailChannels> {
    return loadCustomerEmailChannels(token);
  }

  async updateCustomerEmailChannels(token: string, req: UpdateChannelsRequest): Promise<boolean> {
    return updateCustomerEmailChannels(token, req);
  }

  async optOutAllCustomerEmailChannels(token: string, campaign?: EmailCampaign): Promise<boolean> {
    return optOutAllCustomerEmailChannels(token, campaign);
  }

  //+++++++++++++++ LIKES +++++++++++++++++++++++

  async likeProduct(productId: string, variantId: string): Promise<boolean> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    const productLike = new Like({type: 'ProductExternal', itemId: `${productId}_${variantId}`});
    return like(productLike, this.currentSession);
  };

  async dislikeProduct(productId: string, variantId: string): Promise<boolean> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    const productLike = new Like({type: 'ProductExternal', itemId: `${productId}_${variantId}`});
    return dislike(productLike, this.currentSession);
  };

  async getLikesForCustomer(): Promise<Like[]> {
    if (!this.currentSession) {
      throw new Error("not logged in");
    }
    return getLikes(this.currentSession);
  };

  //+++++++++++++++ INTERNAL +++++++++++++++++++++

  removeCustomerFromStorage(): void {
    try {
      sessionStorage.removeItem(NY_CUSTOMER_KEY)
    } catch (e) {
      console.error('[customer-service] not able to delete customer from session storage', e);
    }
    try {
      localStorage.removeItem(NY_CUSTOMER_TOKEN);
    } catch (e) {
      console.error('[customer-service] not able to delete customer from local storage', e);
    }
    this.currentCustomer = null;
    this.notifyCustomerSubscribers(this.currentCustomer);
  }

  // noinspection JSMethodCanBeStatic
  private async loadSessionFromApp(): Promise<Session> {
    if (window && window['NY_APP_USER_CONTEXT']) {
      const NYAppContext = window['NY_APP_USER_CONTEXT'];

      // noinspection TypeScriptValidateJSTypes
      const customerId: string = NYAppContext.userId();
      // noinspection TypeScriptValidateJSTypes
      const sessionId: string = NYAppContext.sessionToken();

      if (customerId && sessionId) {
        return new Session({token: sessionId, customerId: customerId})
      } else {
        console.warn('[customer-service] no app login');
      }
    } else {
      console.warn('[customer-service] no NY_APP_USER_CONTEXT present, falling back to local storage');
    }
    return null;
  }

  // noinspection JSMethodCanBeStatic
  private loadFromLocalStorage(): Session {
    const session = localStorage.getItem(NY_CUSTOMER_TOKEN);
    if (session) {
      return new Session(JSON.parse(session));
    }
    return null;
  }

  // noinspection JSMethodCanBeStatic
  private saveToLocalStorage(session: Session) {
    const json = JSON.stringify(session);
    localStorage.setItem(NY_CUSTOMER_TOKEN, json)
  }

  // noinspection JSMethodCanBeStatic
  private loadFromSessionStorage(): Session {
    try {
      const sessionItem = sessionStorage.getItem(NY_CUSTOMER_KEY);
      if (sessionItem) {
        return new Session(JSON.parse(sessionItem));
      }
    } catch (e) {
      console.error("[customer-service] error loading session from sessionStorage: ", e);
    }
    return null;
  }

  // noinspection JSMethodCanBeStatic
  private saveToSessionStorage(session: Session): void {
    const sessionData = JSON.stringify(session);
    sessionStorage.setItem(NY_CUSTOMER_KEY, sessionData);
  }

  private notifyCustomerSubscribers(next: Customer) {
    for (const tag in this.customerSubscriberMap) {
      if (this.customerSubscriberMap.hasOwnProperty(tag)) {
        try {
          this.customerSubscriberMap[tag](next);
        } catch (e) {
          console.error('[customer-service] bad customer-subscriber: ', e);
        }
      }
    }
  }

  private notifyCustomerNaSubscribers() {
    for (const tag in this.customerNaSubscriberMap) {
      if (this.customerNaSubscriberMap.hasOwnProperty(tag)) {
        try {
          this.customerNaSubscriberMap[tag]();
        } catch (e) {
          console.error('[customer-service] bad customerNA-subscriber: ', e);
        }
      }
    }
  }
}
